import { useCallback, useState } from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { LoadingOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useFormDirtyContext } from '../../context/FormDirtyContext';
import { useNetworkStatus } from '../../context/NetworkStatusContext';
import { useVersionCheck } from '../../hooks/useVersionCheck';

const { Text, Paragraph } = Typography;

interface UpdateBannerProps {
  /**
   * Polling interval in milliseconds.
   * @default 300000 (5 minutes)
   */
  pollInterval?: number;
}

/**
 * A non-intrusive banner that appears at the top of the viewport for:
 * 1. Connection issues — when the server is unreachable or network is unstable
 * 2. New version available — when a new deployment is detected
 *
 * Connection issue takes priority over the version update banner.
 */
export function UpdateBanner({ pollInterval }: UpdateBannerProps) {
  const { isDirty } = useFormDirtyContext();
  const { isUnstable } = useNetworkStatus();
  const { hasNewVersion, serverVersion, reloadToUpdate } = useVersionCheck({
    pollInterval,
    enabled: true
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleUpdateClick = useCallback(() => {
    if (isDirty) {
      setShowConfirmModal(true);
    } else {
      reloadToUpdate();
    }
  }, [isDirty, reloadToUpdate]);

  const handleConfirmReload = useCallback(() => {
    setShowConfirmModal(false);
    reloadToUpdate();
  }, [reloadToUpdate]);

  const handleCancelReload = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  // Connection issue banner takes priority
  if (isUnstable) {
    return (
      <Alert
        type="warning"
        banner
        showIcon={false}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          textAlign: 'center'
        }}
        message={
          <Space>
            <LoadingOutlined />
            <span>
              Connection unstable — reconnecting to server...
            </span>
          </Space>
        }
      />
    );
  }

  // Don't render anything if no new version
  if (!hasNewVersion) {
    return null;
  }

  return (
    <>
      <Alert
        type="info"
        banner
        showIcon={false}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          cursor: 'pointer',
          textAlign: 'center'
        }}
        message={
          <Space>
            <ReloadOutlined />
            <span>
              A new version is available
              {serverVersion?.version ? ` (${serverVersion.version})` : ''}.
            </span>
            <Button type="link" size="small" onClick={handleUpdateClick} style={{ padding: 0 }}>
              Click to reload
            </Button>
          </Space>
        }
        onClick={handleUpdateClick}
      />

      {/* Confirmation modal for unsaved changes */}
      <Modal
        open={showConfirmModal}
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            <span>Unsaved Changes</span>
          </Space>
        }
        onCancel={handleCancelReload}
        footer={[
          <Button key="cancel" onClick={handleCancelReload}>
            Continue Working
          </Button>,
          <Button key="reload" type="primary" danger icon={<ReloadOutlined />} onClick={handleConfirmReload}>
            Discard & Reload
          </Button>
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>
            A new version of the application is available and needs to reload.
          </Paragraph>
          <Paragraph type="warning">
            <Text strong>You have unsaved changes.</Text> If you reload now, your unsaved work will be lost.
          </Paragraph>
          <Paragraph type="secondary">
            Click "Continue Working" to save your changes first, then reload manually.
          </Paragraph>
        </Space>
      </Modal>
    </>
  );
}

export default UpdateBanner;
